import { useQuery } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from 'supabase/database.types'
import { ProductItem } from '../message_components/ProductSelectionMessage/types'

const STALE_TIME_24_HOURS = 24 * 60 * 60 * 1000

type SupabaseClientType = SupabaseClient<Database>

const sortByPopular = (
  productIds: number[],
  productMap: Map<
    number,
    { klayder_recommends: boolean; price: number | null }
  >,
  likeCountsMap: Map<number, number>
): number[] => {
  return [...productIds].sort((a, b) => {
    const aData = productMap.get(a)
    const bData = productMap.get(b)
    const aKlayder = aData?.klayder_recommends ?? false
    const bKlayder = bData?.klayder_recommends ?? false
    const aLikes = likeCountsMap.get(a) || 0
    const bLikes = likeCountsMap.get(b) || 0

    const aTier = aKlayder && aLikes > 0 ? 1 : aKlayder ? 2 : aLikes > 0 ? 3 : 4
    const bTier = bKlayder && bLikes > 0 ? 1 : bKlayder ? 2 : bLikes > 0 ? 3 : 4

    if (aTier !== bTier) return aTier - bTier
    if (aLikes !== bLikes) return bLikes - aLikes
    return 0
  })
}

const getProductsByCategoryNameQueryFn = async (
  supabase: SupabaseClientType,
  categoryName: string
): Promise<ProductItem[]> => {
  const { data: categories, error: categoryError } = await supabase
    .from('category')
    .select('id')
    .ilike('title', `%${categoryName}%`)

  if (categoryError) throw categoryError
  if (!categories || categories.length === 0) return []

  const categoryIds = categories.map((c) => c.id)

  const { data: allCategoryProducts, error } = await supabase
    .from('category_product')
    .select('product_id')
    .in('category_id', categoryIds)

  if (error) throw error

  const allProductIdsRaw = (allCategoryProducts || [])
    .map((cp) => cp.product_id)
    .filter((id): id is number => id !== null)
  const allProductIds = [...new Set(allProductIdsRaw)]

  if (allProductIds.length === 0) return []

  // @ts-ignore - TODO1
  const { data: productsData } = await supabase
    // @ts-ignore - TODO1
    .from('product_open')
    .select('id, klayder_recommends, price')
    .in('id', allProductIds)
    .or('is_draft.is.null,is_draft.eq.false')
    .order('klayder_recommends', { ascending: false })

  const productMap = new Map<
    number,
    { klayder_recommends: boolean; price: number | null }
  >()
  productsData?.forEach((p) => {
    if (p.id != null) {
      productMap.set(p.id, {
        klayder_recommends: p.klayder_recommends === true,
        price: p.price ?? null,
      })
    }
  })

  const filteredProductIds = allProductIds.filter((id) => productMap.has(id))

  const { data: likesData } = await supabase
    .from('likes')
    .select('product_id')
    .in('product_id', filteredProductIds)
    .eq('like', true)

  const likeCountsMap = new Map<number, number>()
  likesData?.forEach((like) => {
    if (like.product_id !== null) {
      const currentCount = likeCountsMap.get(like.product_id) || 0
      likeCountsMap.set(like.product_id, currentCount + 1)
    }
  })

  const sortedProductIds = sortByPopular(
    filteredProductIds,
    productMap,
    likeCountsMap
  )

  return sortedProductIds.map((product_id) => ({ product_id }))
}

export const useProductsByCategoryName = (categoryName: string | null) => {
  const supabase = useSupabaseClient()

  const { data: productItems, isLoading } = useQuery({
    queryKey: ['productOverlayCategory', categoryName],
    queryFn: () => getProductsByCategoryNameQueryFn(supabase, categoryName!),
    enabled: !!categoryName,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME_24_HOURS,
  })

  return {
    productItems: productItems ?? [],
    isLoading,
  }
}
